import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';
import { relationshipCompaniesApi } from '@/features/relationshipCompanies/relationshipCompaniesApi';
import { refreshTypesenseTables } from '@/features/table/refreshTypesenseTables';

const toRelationshipContactResponse = (response) => ({
	...(response?.data ?? {}),
	updatedRelationshipCompanyIds: response?.updatedRelationshipCompanyIds ?? [],
});

const invalidateRelationshipCompanies = (dispatch, companyIds) => {
	if (!companyIds.length) return;

	dispatch(
		relationshipCompaniesApi.util.invalidateTags(
			companyIds.map((id) => ({ type: 'RELATIONSHIP_COMPANY', id })),
		),
	);
};

export const relationshipContactsApi = createApi({
	reducerPath: 'relationshipContactsApi',
	tagTypes: ['RELATIONSHIP_CONTACTS', 'RELATIONSHIP_CONTACT'],
	baseQuery: fetchBaseQuery({
		...BASE_QUERY,
		baseUrl: process.env.REACT_APP_RELATIONSHIP_CONTACT_API,
	}),
	endpoints: (builder) => ({
		listRelationshipContacts: builder.query({
			providesTags: (result, error, arg) => [
				{ type: 'RELATIONSHIP_CONTACTS', id: 'LIST' },
				...(arg?.relationshipCompanyId
					? [{ type: 'RELATIONSHIP_CONTACTS', id: arg.relationshipCompanyId }]
					: []),
				...(result ?? []).map((contact) => ({
					type: 'RELATIONSHIP_CONTACT',
					id: contact.id,
				})),
			],
			query: ({
				relationshipCompanyId,
				relationshipType,
				status,
				createdBy,
				includeArchived,
				limit,
			} = {}) => ({
				url: '/relationshipContacts/list',
				method: 'POST',
				body: {
					relationshipCompanyId,
					relationshipType,
					status,
					createdBy,
					includeArchived,
					limit,
				},
			}),
			transformResponse: (response) => response?.data ?? [],
		}),
		getRelationshipContact: builder.query({
			providesTags: (result, error, { relationshipContactId }) => [
				{ type: 'RELATIONSHIP_CONTACT', id: relationshipContactId },
			],
			query: ({ relationshipContactId }) => ({
				url: '/relationshipContacts/get',
				method: 'POST',
				body: { relationshipContactId },
			}),
			transformResponse: (response) => response?.data,
		}),
		createRelationshipContact: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'RELATIONSHIP_CONTACTS', id: 'LIST' },
				...(arg?.relationshipCompanyId
					? [{ type: 'RELATIONSHIP_CONTACTS', id: arg.relationshipCompanyId }]
					: []),
			],
			query: ({ properties, relationshipCompanyId, isPrimary }) => ({
				url: '/relationshipContacts/create',
				method: 'POST',
				body: { properties, relationshipCompanyId, isPrimary },
			}),
			transformResponse: toRelationshipContactResponse,
			onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled;
					invalidateRelationshipCompanies(dispatch, data?.updatedRelationshipCompanyIds);
					refreshTypesenseTables(dispatch);
				} catch {
					//
				}
			},
		}),
		updateRelationshipContact: builder.mutation({
			invalidatesTags: (result, error, { relationshipContactId, relationshipCompanyId }) => [
				{ type: 'RELATIONSHIP_CONTACTS', id: 'LIST' },
				{ type: 'RELATIONSHIP_CONTACT', id: relationshipContactId },
				...(relationshipCompanyId
					? [{ type: 'RELATIONSHIP_CONTACTS', id: relationshipCompanyId }]
					: []),
			],
			query: ({
				relationshipContactId,
				properties,
				relationshipCompanyId,
				isPrimary,
			}) => ({
				url: '/relationshipContacts/update',
				method: 'POST',
				body: { relationshipContactId, properties, relationshipCompanyId, isPrimary },
			}),
			transformResponse: toRelationshipContactResponse,
			onQueryStarted: async (
				{ relationshipContactId, properties },
				{ dispatch, queryFulfilled, getState },
			) => {
				const existing = relationshipContactsApi.endpoints.getRelationshipContact.select({
					relationshipContactId,
				})(getState())?.data;
				const patchResult = existing
					? dispatch(
							relationshipContactsApi.util.updateQueryData(
								'getRelationshipContact',
								{ relationshipContactId },
								(draft) => {
									Object.assign(draft, properties);
								},
							),
						)
					: null;
				if (!existing) {
					dispatch(
						relationshipContactsApi.util.upsertQueryData(
							'getRelationshipContact',
							{ relationshipContactId },
							{ id: relationshipContactId, ...properties },
						),
					);
				}
				try {
					const { data } = await queryFulfilled;
					if (data) {
						const { updatedRelationshipCompanyIds, ...contact } = data;
						dispatch(
							relationshipContactsApi.util.updateQueryData(
								'getRelationshipContact',
								{ relationshipContactId },
								(draft) => {
									Object.assign(draft, contact);
								},
							),
						);
						invalidateRelationshipCompanies(dispatch, updatedRelationshipCompanyIds);
					}
					refreshTypesenseTables(dispatch);
				} catch {
					patchResult?.undo();
				}
			},
		}),
		deleteRelationshipContact: builder.mutation({
			invalidatesTags: (result, error, { relationshipContactId, relationshipCompanyId }) => [
				{ type: 'RELATIONSHIP_CONTACTS', id: 'LIST' },
				{ type: 'RELATIONSHIP_CONTACT', id: relationshipContactId },
				...(relationshipCompanyId
					? [{ type: 'RELATIONSHIP_CONTACTS', id: relationshipCompanyId }]
					: []),
			],
			query: ({ relationshipContactId, relationshipCompanyId }) => ({
				url: '/relationshipContacts/delete',
				method: 'POST',
				body: { relationshipContactId, relationshipCompanyId },
			}),
			transformResponse: (response) => response?.data,
			onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					refreshTypesenseTables(dispatch);
				} catch {
					//
				}
			},
		}),
	}),
});

export const {
	useListRelationshipContactsQuery,
	useGetRelationshipContactQuery,
	useCreateRelationshipContactMutation,
	useUpdateRelationshipContactMutation,
	useDeleteRelationshipContactMutation,
} = relationshipContactsApi;
